import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../utils/PMDialog";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { createTool, updateTool } from "../../../api/tools";
import { PMField } from "../../utils/PMField";
import PMCombobox from "../../utils/PMCombobox";
import { ModernSupplierSelectorField } from "../../utils/moduleSelectors/ModernSupplierSelectorField";

function formatApiErrors(error) {
  const d = error?.response?.data;
  if (Array.isArray(d?.errors)) return d.errors.filter(Boolean).join("\n");
  if (typeof d?.errors === "string") return d.errors;
  if (d?.error && typeof d.error === "string") return d.error;
  if (error?.message) return error.message;
  return null;
}

function normalizeToolPayload(values, isEdit) {
  const out = { ...values };
  if (!isEdit && (!out.tool_number || String(out.tool_number).trim() === "")) {
    delete out.tool_number;
  }
  ["tool_category_id", "tool_brand_id"].forEach((k) => {
    const v = out[k];
    if (v === "" || v === 0 || v === "0") out[k] = null;
  });
  if (out.supplier_id === "" || out.supplier_id === 0 || out.supplier_id === "0") {
    out.supplier_id = null;
  }
  if (out.purchase_date === "") out.purchase_date = null;
  if (out.purchase_price === "") out.purchase_price = null;
  if (out.service_interval_days === "") out.service_interval_days = null;
  return out;
}

const ToolDialog = ({
  open,
  onClose,
  tool = null,
  categories = [],
  brands = [],
  onToolCreated,
  onToolUpdated,
  suggestedNextNumber = "",
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const isEdit = Boolean(tool?.id);

  const statusOptions = [
    { value: "active", title: t("tool.status.active") },
    { value: "inactive", title: t("tool.status.inactive") },
    { value: "maintenance", title: t("tool.status.maintenance") },
    { value: "retired", title: t("tool.status.retired") },
  ];

  const categoryOptions = categories.map((cat) => ({
    value: cat.id,
    title: cat.name,
  }));

  const brandOptions = brands.map((brand) => ({
    value: brand.id,
    title: brand.name,
  }));

  const initialValues = useMemo(
    () => ({
      title: tool?.title || "",
      tool_number: tool?.tool_number || "",
      description: tool?.description || "",
      status: tool?.status || "active",
      active: tool?.active !== undefined ? tool.active : true,
      model: tool?.model || "",
      serial_number: tool?.serial_number || "",
      purchase_date: tool?.purchase_date || "",
      purchase_price: tool?.purchase_price ?? "",
      service_interval_days: tool?.service_interval_days ?? "",
      tool_category_id: tool?.tool_category_id || tool?.category?.id || "",
      tool_brand_id: tool?.tool_brand_id || tool?.brand?.id || "",
      supplier_id: tool?.supplier_id ?? tool?.supplier?.id ?? null,
    }),
    [tool]
  );

  const supplierSelected = useMemo(() => {
    if (tool?.supplier) return tool.supplier;
    if (tool?.supplier_id) return { id: tool.supplier_id };
    return null;
  }, [tool]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        title: Yup.string().required(t("tool.title") + " " + t("is required")),
        tool_number: isEdit
          ? Yup.string().required(t("tool.number") + " " + t("is required"))
          : Yup.string().nullable(),
        status: Yup.string().required(t("tool.status") + " " + t("is required")),
      }),
    [isEdit, t]
  );

  const handleSubmit = async (values, helpers) => {
    setIsLoading(true);
    try {
      const payload = normalizeToolPayload(values, isEdit);
      let result;
      if (isEdit) {
        result = await updateTool(tool.id, payload);
        toast.success(t("tool.tool_updated"));
        onToolUpdated?.(result.data);
      } else {
        result = await createTool(payload);
        toast.success(t("tool.tool_created"));
        onToolCreated?.(result.data);
      }

      onClose();
    } catch (error) {
      console.error("Error saving tool:", error);
      const msg = formatApiErrors(error);
      toast.error(msg || t("Failed to save tool"));
    } finally {
      setIsLoading(false);
      helpers.setSubmitting(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={isEdit ? t("tool.edit_tool") : t("tool.add_tool")}
      size="2xl"
    >
      <Formik
        key={isEdit ? `tool-${tool.id}` : "tool-new"}
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <Form className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PMField
                name="title"
                label={t("tool.title")}
                type="text"
                required
                {...formik}
              />

              <PMField
                name="tool_number"
                label={t("tool.number")}
                type="text"
                required={isEdit}
                placeholder={
                  !isEdit && suggestedNextNumber
                    ? t("tool.number_auto_placeholder", {
                        next: suggestedNextNumber,
                      })
                    : undefined
                }
                {...formik}
              />

              <PMCombobox
                name="tool_brand_id"
                label={t("tool.brand")}
                options={brandOptions}
                {...formik}
              />

              <ModernSupplierSelectorField
                name="supplier_id"
                label="tool.supplier.label"
                selected={supplierSelected}
                setFieldValue={formik.setFieldValue}
                errors={formik.errors}
              />

              <PMField
                name="model"
                label={t("tool.model")}
                type="text"
                {...formik}
              />

              <PMField
                name="serial_number"
                label={t("tool.serial_number")}
                type="text"
                {...formik}
              />

              <PMField
                name="purchase_date"
                label={t("tool.purchase_date")}
                type="date"
                {...formik}
              />

              <PMField
                name="purchase_price"
                label={t("tool.purchase_price")}
                type="number"
                step="0.01"
                {...formik}
              />

              <PMField
                name="service_interval_days"
                label={t("tool.service_interval")}
                type="number"
                {...formik}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PMCombobox
                name="status"
                label={t("tool.status")}
                options={statusOptions}
                {...formik}
              />

              <PMCombobox
                name="tool_category_id"
                label={t("tool.category")}
                options={categoryOptions}
                {...formik}
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="active"
                checked={formik.values.active}
                onChange={formik.handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">
                {t("Active")}
              </label>
            </div>

            <PMField
              name="description"
              label={t("tool.description")}
              multiline
              rows={4}
              {...formik}
            />

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {t("Cancel")}
              </button>
              <button
                type="submit"
                disabled={isLoading || formik.isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isLoading || formik.isSubmitting
                  ? isEdit
                    ? t("Updating...")
                    : t("Creating...")
                  : isEdit
                  ? t("Update Tool")
                  : t("Create Tool")}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </PMDialog>
  );
};

export default ToolDialog;
